import { AlertStatus } from '@/gen/inventory/v1/alert_pb'

import { ACTIVE_COMPANY_LIFECYLES } from '@/const/company-lifecycles'
import { alertStatusLabel, companyLifecycleLabel } from '@/const/label'

import { UrlParam } from '@/lib/url/url-param.hook'

import {
  ALERTS_URL_PREFIX,
  ALERT_COLUMN_IDS,
} from '@/pages/alert-inventory/ag-alert-inventory-table/consts'
import { DISCOVERY_COLUMN_IDS } from '@/pages/discovered/ag-grid-solutions/consts'
import { DETECTED_TAB, DISCOVERY_DETECTED_PREFIX } from '@/pages/discovered/consts'
import { SettingsSection } from '@/pages/settings-page/types'
import {
  INVENTORY_COLUMN_IDS,
  INVENTORY_PREFIX,
} from '@/pages/third-party-inventory/ag-grid-inventory/types'

import { CANONICAL_PARAM_KEYS } from '@/components/lemmala-design-system/table/consts'
import { AI_ONLY_POSTURE_LABELS } from '@/components/shared/ai-posture-badge/column'

type QueryValue = string | number | boolean | null | undefined
type QueryParams = Record<string, QueryValue | QueryValue[]>

type BuildUrlOptions = {
  absolute?: boolean
}

export const AppRoute = {
  LOGIN: '/login',
  OVERVIEW: '/overview',
  DASHBOARDS: '/dashboards',
  DASHBOARD_NEW: '/dashboards/new',
  DASHBOARD_VIEW: '/dashboards/:dashboardId',
  DASHBOARD_EDIT: '/dashboards/:dashboardId/edit',
  PROCUREMENT_REQUESTS: '/procurement-requests',
  ASSESSMENTS: '/assessments',
  ASSESSMENT: '/assessments/:assessmentId',
  THIRD_PARTIES: '/third-parties',
  THIRD_PARTY: '/third-parties/:thirdPartyId',
  ENGAGEMENTS: '/engagements',
  FOURTH_PARTIES: '/fourth-parties',
  DISCOVERED: '/discovery',
  MONITORING: '/monitoring',
  SIGNAL_FEED: '/monitoring/signal-feed',
  SETTINGS: '/settings',
  PAST_ASSESSMENT: '/past-assessment/:assessmentId',
  INTEGRATION_SETUP: '/integrations/:integrationId',
  QUESTIONNAIRE: '/questionnaires/:questionnaireId',
  QUESTIONNAIRE_TEMPLATE: '/questionnaires/templates/:templateId',
  COMPANY_REQUEST: '/requests',
  PRODUCTLANE_SSO: '/productlane-sso',
  DOCS_LOGIN: '/docs-login',
  DOCS_SSO: '/docs-sso',
  QUESTIONNAIRE_PORTAL: '/q/:portalId/*',
  PREVIEW_QUESTIONNAIRE: '/preview-questionnaire/:questionnaireId',
  STORYBOOK: '/storybook',
  AI_GOVERNANCE: '/ai-governance',
  OUTREACH: '/outreach',
  NEW_OUTREACH_CAMPAIGN: '/outreach/campaigns/new',
  OUTREACH_CAMPAIGN: '/outreach/campaigns/:campaignId',
} as const

export const buildAppUrl = (
  pathname: string,
  query: QueryParams = {},
  { absolute = false }: BuildUrlOptions = {},
) => {
  const searchParams = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    if (value == null || value === '') continue

    if (Array.isArray(value)) {
      value
        .filter((item) => item != null && item !== '')
        .forEach((item) => searchParams.append(key, String(item)))
    } else {
      searchParams.append(key, String(value))
    }
  }

  const search = searchParams.toString()
  const relativeUrl = search ? `${pathname}?${search}` : pathname

  return absolute ? new URL(relativeUrl, window.location.origin).href : relativeUrl
}

export const buildThirdPartiesUrl = (query?: QueryParams, options?: BuildUrlOptions) =>
  buildAppUrl(AppRoute.THIRD_PARTIES, query, options)

// Mirrors the dashboard chart queries, which exclude archived + offboarded so
// the drill-down inventory matches the chart count.
const buildThirdPartiesUrlWithSetFilter = (
  columnId: string,
  values: string[],
  options?: BuildUrlOptions,
) => {
  const filterModel = {
    [columnId]: { filterType: 'set', values },
    [INVENTORY_COLUMN_IDS.lifecycle]: {
      filterType: 'set',
      values: ACTIVE_COMPANY_LIFECYLES.map((lifecycle) => companyLifecycleLabel[lifecycle]),
    },
  }
  return buildThirdPartiesUrl(
    {
      [`${INVENTORY_PREFIX}_${CANONICAL_PARAM_KEYS.filter}`]: encodeURIComponent(
        JSON.stringify(filterModel),
      ),
    },
    options,
  )
}

export const buildThirdPartiesUrlWithAiCapabilityFilter = (
  capabilityLabel: string,
  options?: BuildUrlOptions,
) =>
  buildThirdPartiesUrlWithSetFilter(INVENTORY_COLUMN_IDS.aiCapabilities, [capabilityLabel], options)

export const buildThirdPartiesUrlWithAiPostureFilter = (
  postureLabel: string,
  options?: BuildUrlOptions,
) => buildThirdPartiesUrlWithSetFilter(INVENTORY_COLUMN_IDS.aiPosture, [postureLabel], options)

export const buildDiscoveredUrlWithAiPostureFilter = (
  postureLabel: string,
  options?: BuildUrlOptions,
) => {
  const filterModel = {
    [DISCOVERY_COLUMN_IDS.aiPosture]: { filterType: 'set', values: [postureLabel] },
  }
  return buildAppUrl(
    AppRoute.DISCOVERED,
    {
      [UrlParam.VIEW]: DETECTED_TAB,
      [`${DISCOVERY_DETECTED_PREFIX}_${CANONICAL_PARAM_KEYS.filter}`]: encodeURIComponent(
        JSON.stringify(filterModel),
      ),
    },
    options,
  )
}

const buildAiThirdPartiesInventoryFilterModel = (
  extra: Record<string, { filterType: 'set'; values: string[] }> = {},
) => ({
  [INVENTORY_COLUMN_IDS.aiPosture]: {
    filterType: 'set',
    values: AI_ONLY_POSTURE_LABELS,
  },
  [INVENTORY_COLUMN_IDS.lifecycle]: {
    filterType: 'set',
    values: ACTIVE_COMPANY_LIFECYLES.map((lifecycle) => companyLifecycleLabel[lifecycle]),
  },
  ...extra,
})

export const buildAiThirdPartiesUrl = (options?: BuildUrlOptions) =>
  buildThirdPartiesUrl(
    {
      [`${INVENTORY_PREFIX}_${CANONICAL_PARAM_KEYS.filter}`]: encodeURIComponent(
        JSON.stringify(buildAiThirdPartiesInventoryFilterModel()),
      ),
    },
    options,
  )

export const buildThirdPartiesUrlWithAiAssessmentScoreFilter = (
  assessmentScoreLabel: string,
  options?: BuildUrlOptions,
) => {
  // Mirrors the ListAIThirdPartyCountsByAssessmentScore population: AI-classified vendors
  // (model provider, AI native, embedded AI) whose lifecycle is not archived/offboarded,
  // bucketed by their current assessment score.
  const filterModel = buildAiThirdPartiesInventoryFilterModel({
    [INVENTORY_COLUMN_IDS.assessmentScore]: {
      filterType: 'set' as const,
      values: [assessmentScoreLabel],
    },
  })
  return buildThirdPartiesUrl(
    {
      [`${INVENTORY_PREFIX}_${CANONICAL_PARAM_KEYS.filter}`]: encodeURIComponent(
        JSON.stringify(filterModel),
      ),
    },
    options,
  )
}

const buildOpenAiAlertsFilterModel = (
  extra: Record<string, { filterType: 'set'; values: string[] }> = {},
) => ({
  [ALERT_COLUMN_IDS.status]: {
    filterType: 'set' as const,
    values: [alertStatusLabel[AlertStatus.OPEN], alertStatusLabel[AlertStatus.ONGOING]],
  },
  [ALERT_COLUMN_IDS.aiPosture]: {
    filterType: 'set' as const,
    values: AI_ONLY_POSTURE_LABELS,
  },
  ...extra,
})

const buildAlertsUrlWithFilterModel = (
  filterModel: Record<string, { filterType: 'set'; values: string[] }>,
  options?: BuildUrlOptions,
) =>
  buildAppUrl(
    AppRoute.MONITORING,
    {
      [`${ALERTS_URL_PREFIX}_${CANONICAL_PARAM_KEYS.filter}`]: encodeURIComponent(
        JSON.stringify(filterModel),
      ),
    },
    options,
  )

export const buildOpenAiAlertsUrl = (options?: BuildUrlOptions) =>
  buildAlertsUrlWithFilterModel(buildOpenAiAlertsFilterModel(), options)

export const buildAlertsUrlWithAiPostureSeverityFilter = (
  severityLabel: string,
  options?: BuildUrlOptions,
) =>
  // Mirrors the ListAIThirdPartyAlertCountsBySeverity population: open + ongoing
  // alerts on AI-classified vendors (model provider, AI native, embedded AI).
  buildAlertsUrlWithFilterModel(
    buildOpenAiAlertsFilterModel({
      [ALERT_COLUMN_IDS.severity]: { filterType: 'set', values: [severityLabel] },
    }),
    options,
  )

export const buildThirdPartyUrl = (
  thirdPartyId: string,
  query?: QueryParams,
  options?: BuildUrlOptions,
) => buildAppUrl(`/third-parties/${thirdPartyId}`, query, options)

// Engagements are shown as a drawer over the third-party page, keyed by ?engagementId=…
export const buildEngagementUrl = (
  thirdPartyId: string,
  engagementId: string,
  query?: QueryParams,
  options?: BuildUrlOptions,
) => buildThirdPartyUrl(thirdPartyId, { ...query, engagementId }, options)

export const buildFourthPartiesUrl = (query?: QueryParams, options?: BuildUrlOptions) =>
  buildAppUrl(AppRoute.FOURTH_PARTIES, query, options)

// Search-only href for opening the layout-mounted FourthPartyDrawer without
// navigating away from the current page. Used as a react-router <Link to=…/>
// target, which resolves it against the current pathname.
export const buildFourthPartyDrawerHref = (fourthPartyCompanyId: string) =>
  `?${UrlParam.FOURTH_PARTY_COMPANY_ID}=${encodeURIComponent(fourthPartyCompanyId)}`

export const buildSignalFeedUrl = (options?: BuildUrlOptions) =>
  buildAppUrl(AppRoute.SIGNAL_FEED, undefined, options)

export const buildAssessmentsUrl = (query?: QueryParams, options?: BuildUrlOptions) =>
  buildAppUrl(AppRoute.ASSESSMENTS, query, options)

export const buildAssessmentUrl = (
  assessmentId: string,
  query?: QueryParams,
  options?: BuildUrlOptions,
) => buildAppUrl(`/assessments/${assessmentId}`, query, options)

export const buildPastAssessmentUrl = (
  assessmentId: string,
  query?: QueryParams,
  options?: BuildUrlOptions,
) => buildAppUrl(`/past-assessment/${assessmentId}`, query, options)

export const buildSettingsUrl = (query?: QueryParams, options?: BuildUrlOptions) =>
  buildAppUrl(AppRoute.SETTINGS, query, options)

export const buildSettingsSectionUrl = (
  section: SettingsSection,
  query?: QueryParams,
  options?: BuildUrlOptions,
) => buildAppUrl(`${AppRoute.SETTINGS}/${section}`, query, options)

export const buildIntegrationSetupUrl = (integrationId: string, options?: BuildUrlOptions) =>
  buildAppUrl(`/integrations/${integrationId}`, undefined, options)

export const buildDashboardUrl = (dashboardId: string, options?: BuildUrlOptions) =>
  buildAppUrl(`${AppRoute.DASHBOARDS}/${dashboardId}`, undefined, options)

export const buildDashboardEditUrl = (dashboardId: string, options?: BuildUrlOptions) =>
  buildAppUrl(`${AppRoute.DASHBOARDS}/${dashboardId}/edit`, undefined, options)

export const buildOutreachUrl = (options?: BuildUrlOptions) =>
  buildAppUrl(AppRoute.OUTREACH, undefined, options)

export const buildNewOutreachCampaignUrl = (options?: BuildUrlOptions) =>
  buildAppUrl(AppRoute.NEW_OUTREACH_CAMPAIGN, undefined, options)

export const buildOutreachCampaignUrl = (campaignId: string, options?: BuildUrlOptions) =>
  buildAppUrl(`/outreach/campaigns/${campaignId}`, undefined, options)

export const buildQuestionnaireTemplateUrl = (templateId: string, options?: BuildUrlOptions) =>
  buildAppUrl(`/questionnaires/templates/${templateId}`, undefined, options)

export const buildQuestionnairePortalUrl = (
  portalId: string,
  query?: QueryParams,
  options?: BuildUrlOptions,
) => buildAppUrl(`/q/${portalId}`, query, options)

export const buildPreviewQuestionnaireUrl = (questionnaireId: string, options?: BuildUrlOptions) =>
  buildAppUrl(`/preview-questionnaire/${questionnaireId}`, undefined, options)
